/* ==========================================================================
   PBL product card - Sep 2026 rebuild
   Markup: snippets/card-product-pbl.liquid   Styles: assets/pbl-card.css

   1. <pbl-card-form>  AJAX add to cart with Adding... / Added / SOLD OUT states.
      Opens UpCart when it is on the page, otherwise the theme's cart-drawer.
   2. Swatch relocation: SA Variants (Combined Listings) injects
      <variant-king-combined-listing> after .price. If the app ever drops it
      anywhere else inside the card, move it into .pbl-card__row so it sits on
      the price row. A MutationObserver covers cards added by infinite scroll
      and sliders.
   ========================================================================== */
(function () {
  'use strict';

  var ADDED_MS = 1600;
  var UNCONFIRMED_MESSAGE = "We couldn't confirm this item was added. Check your cart before trying again.";

  /* ---------------------------------------------------------------------- */
  /* Add to cart                                                             */
  /* ---------------------------------------------------------------------- */
  function openCart(response, sections) {
    // UpCart intercepts /cart/add itself; refresh + open covers the cases where
    // its interceptor is skipped (e.g. an older cached bundle).
    if (typeof window.upcartOpenCart === 'function') {
      if (typeof window.upcartRefreshCart === 'function') {
        try { window.upcartRefreshCart(); } catch (e) { /* noop */ }
      }
      try { window.upcartOpenCart(); } catch (e) { /* noop */ }
      return;
    }

    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function' && response && response.sections) {
      if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
      drawer.renderContents(response);
      return;
    }

    var notification = document.querySelector('cart-notification');
    if (notification && typeof notification.renderContents === 'function' && response && response.sections) {
      notification.renderContents(response);
    }
  }

  function sectionsToRender() {
    var ids = [];
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      try {
        drawer.getSectionsToRender().forEach(function (s) { if (s && s.id) ids.push(s.id); });
      } catch (e) { /* noop */ }
    }
    var notification = document.querySelector('cart-notification');
    if (notification && typeof notification.getSectionsToRender === 'function') {
      try {
        if (typeof window.loadTemplateContent === 'function') window.loadTemplateContent(notification);
        notification.getSectionsToRender().forEach(function (s) { if (s && s.id) ids.push(s.id); });
      } catch (e) { /* noop */ }
    }
    return ids.filter(function (id, i, a) { return a.indexOf(id) === i; });
  }

  function notifyCart(variantId, response) {
    // global.js uses a lexical const, so PUB_SUB_EVENTS is not on window.
    var events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS;
    var publisher = typeof publish === 'function' ? publish : window.publish;
    if (!events || typeof publisher !== 'function') return;

    var data = { source: 'pbl-card-form', productVariantId: variantId, cartData: response };
    // The theme only remaps product-form updates to cart-add. Custom cards need
    // both the quantity/cart subscribers and the document's cart-add consumers.
    ['cartUpdate', 'cartAdd'].forEach(function (event) {
      if (!events[event]) return;
      try {
        var pending = publisher(events[event], data);
        if (pending && typeof pending.catch === 'function') pending.catch(function () { /* noop */ });
      } catch (e) { /* A subscriber must not turn a successful add into a failure. */ }
    });
  }

  class PblCardForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.button = this.querySelector('.pbl-card__atc');
      this.label = this.querySelector('.pbl-card__atc-label');
      this.errorMessage = this.querySelector('.pbl-card__error');
      this.busy = false;
      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    setState(state) {
      if (!this.button) return;
      this.button.dataset.state = state;
      var text = this.button.getAttribute('data-label-' + state);
      if (text && this.label) this.label.textContent = text;
      var lock = state === 'adding' || state === 'soldout';
      this.button.setAttribute('aria-disabled', lock ? 'true' : 'false');
      if (state === 'soldout') this.button.disabled = true;
    }

    showError(message) {
      // Keep feedback available even if a cached card lacks the new markup.
      if (!this.errorMessage && this.form) {
        this.errorMessage = document.createElement('p');
        this.errorMessage.className = 'pbl-card__error';
        this.errorMessage.setAttribute('role', 'alert');
        this.errorMessage.setAttribute('aria-atomic', 'true');
        this.form.appendChild(this.errorMessage);
      }
      if (this.errorMessage) {
        this.errorMessage.textContent = message || '';
        this.errorMessage.hidden = !message;
      }
      if (message && typeof window.showUpcartErrorToast === 'function') {
        try { window.showUpcartErrorToast(message); } catch (e) { /* Inline feedback remains visible. */ }
      }
    }

    fail(message, soldout) {
      this.setState(soldout ? 'soldout' : 'idle');
      this.showError(message);
      this.busy = false;
    }

    onSubmit(evt) {
      evt.preventDefault();
      if (this.busy || !this.button || this.button.disabled) return;
      this.showError('');

      var idInput = this.form.querySelector('[name="id"]');
      var variantId = idInput ? idInput.value : null;
      if (!variantId) {
        this.fail('This product could not be selected. Open its product page and try again.');
        return;
      }

      this.busy = true;
      this.setState('adding');

      var body = new FormData(this.form);
      var sections = sectionsToRender();
      var useThemeDrawer = typeof window.upcartOpenCart !== 'function' && sections.length > 0;
      if (useThemeDrawer) {
        body.append('sections', sections.join(','));
        body.append('sections_url', window.location.pathname);
      }

      var url = ((window.routes && window.routes.cart_add_url) || '/cart/add').replace(/\.js$/, '') + '.js';
      var self = this;

      fetch(url, {
        method: 'POST',
        body: body,
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (r) {
          return r.json().catch(function () { return null; }).then(function (json) {
            return { ok: r.ok, status: r.status, json: json };
          });
        })
        .then(function (res) {
          var json = res.json;
          var isObject = json && typeof json === 'object' && !Array.isArray(json);
          if (!res.ok || (isObject && json.status)) {
            var message = isObject && typeof json.description === 'string' ? json.description.trim() : '';
            if (!message && isObject && typeof json.message === 'string') message = json.message.trim();
            message = message || 'We could not add this item to your cart. Please try again.';
            var msg = message.toLowerCase();
            var stockError = res.status === 422 || (isObject && Number(json.status) === 422);
            self.fail(message, stockError && (msg.indexOf('sold out') > -1 || msg.indexOf('out of stock') > -1 || msg.indexOf('unavailable') > -1));
            return;
          }

          // An empty or non-JSON response cannot confirm that Shopify added an item.
          var itemId = isObject && (json.variant_id || json.id);
          if (!isObject || (typeof itemId !== 'number' && typeof itemId !== 'string') || !(Number(itemId) > 0)) {
            self.fail(UNCONFIRMED_MESSAGE);
            return;
          }

          notifyCart(variantId, json);
          self.setState('added');
          try { openCart(json, sections); } catch (e) { console.error('[pbl-card] cart display failed', e); }

          setTimeout(function () {
            if (self.button.dataset.state === 'added') self.setState('idle');
            self.busy = false;
          }, ADDED_MS);
        })
        .catch(function (err) {
          console.error('[pbl-card] add to cart failed', err);
          self.fail(UNCONFIRMED_MESSAGE);
        });
    }
  }

  if (!customElements.get('pbl-card-form')) customElements.define('pbl-card-form', PblCardForm);

  /* ---------------------------------------------------------------------- */
  /* Swatch relocation                                                       */
  /* ---------------------------------------------------------------------- */
  function placeSwatches(card) {
    var row = card.querySelector('.pbl-card__row');
    if (!row) return;
    var swatches = card.querySelectorAll('variant-king-combined-listing');
    swatches.forEach(function (el) {
      if (el.parentElement !== row) row.appendChild(el);
    });
  }

  /* Image well click -> product page (the well is a div, see the snippet comment) */
  function wireMedia(card) {
    var media = card.querySelector('.pbl-card__media');
    if (!media || media.dataset.pblWired) return;
    media.dataset.pblWired = '1';
    media.addEventListener('click', function (evt) {
      var link = card.querySelector('.pbl-card__title a');
      if (!link || !link.href) return;
      if (evt.metaKey || evt.ctrlKey || evt.button === 1) {
        window.open(link.href, '_blank');
      } else {
        window.location.href = link.href;
      }
    });
  }

  function scan(root) {
    var cards = root.querySelectorAll ? root.querySelectorAll('.pbl-card') : [];
    cards.forEach(function (c) { placeSwatches(c); wireMedia(c); });
    if (root.classList && root.classList.contains('pbl-card')) { placeSwatches(root); wireMedia(root); }
  }

  function init() {
    scan(document);

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'VARIANT-KING-COMBINED-LISTING') {
            var card = node.closest('.pbl-card');
            if (card) placeSwatches(card);
          } else if (node.querySelector && (node.querySelector('.pbl-card') || node.querySelector('variant-king-combined-listing') || node.classList.contains('pbl-card'))) {
            scan(node);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
